import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection,
  collectionData,
  doc,
  DocumentReference,
  Firestore,
  getDoc,
  updateDoc,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { getDownloadURL, ref, Storage, StorageReference, uploadBytes } from '@angular/fire/storage';
import { extractPdfPageCount } from '@mlc/shared/utils/pdf.utils';
import { firstValueFrom, from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators';
import { PathConstants } from '../../db.paths';
import { Document } from '../models/case.model';
import { AuthService } from './auth.service';

/**
 * Represents the progress state of a file upload.
 */
export interface UploadProgress {
  fileName: string;
  percentage: number; // 0-100
  bytesTransferred: number;
  totalBytes: number;
  status: 'uploading' | 'completed' | 'cancelled' | 'failed';
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private firestore: Firestore = inject(Firestore);
  private authService: AuthService = inject(AuthService);
  private storage: Storage = inject(Storage);
  private functions: Functions = inject(Functions);

  constructor() {}

  getCaseDocuments(caseId: string): Observable<Document[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          return of([]);
        }
        const fileCollectionPath = PathConstants.getFileCollectionPath(user.uid, caseId);
        return collectionData(collection(this.firestore, fileCollectionPath), { idField: 'id' }).pipe(
          map((docs: Document[]) => docs.filter((doc) => !doc.deleted))
        );
      })
    );
  }

  /**
   * Uploads a document to Firebase Storage with progress tracking and cancellation support.
   *
   * @param caseId - The ID of the case to upload the document to
   * @param file - The File object to upload
   * @returns Object containing upload$ observable, progress$ observable, and cancel() function
   *
   * @remarks
   * - Files must be <= 20 MB (20,971,520 bytes)
   * - PDF files will have page count extracted automatically
   * - Non-PDF files will have pageCount set to 1
   * - Progress events are throttled to 100ms intervals
   * - Cancellation can be invoked at any time via the returned cancel() function
   */
  uploadDocument(
    caseId: string,
    file: File
  ): {
    upload$: Observable<{
      downloadURL: string;
      metadata: any;
      docId: string;
      pageCount: number;
      providerResponse?: any;
    }>;
    progress$: Observable<UploadProgress>;
    cancel: () => void;
  } {
    // --- Step 0: Validate file size ---
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
    if (file.size > MAX_FILE_SIZE) {
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      const error = new Error(`File exceeds 20 MB limit. File size: ${fileSizeMB} MB`);

      return {
        upload$: throwError(() => error),
        progress$: of(),
        cancel: () => {}, // No-op since upload hasn't started
      };
    }

    // Hoist variables that are needed across multiple steps
    let userId: string;
    let orgCode: string | undefined;
    let storageRef: StorageReference;
    let filePath: string;
    let safeFileName: string;
    let documentMetadata: Partial<Document>;
    let downloadURL: string;
    let newDocRef: DocumentReference;
    let combinedMetadata: any;
    let pageCount: number = 1;

    // Subjects for progress tracking and cancellation
    const progressSubject = new Subject<UploadProgress>();
    const cancellationSubject = new Subject<void>();

    // Function to cancel the upload
    const cancel = () => {
      progressSubject.next({
        fileName: file.name,
        percentage: 0,
        bytesTransferred: 0,
        totalBytes: file.size,
        status: 'cancelled',
      });
      cancellationSubject.next();
      cancellationSubject.complete();
      progressSubject.complete();
    };

    const upload$ = this.authService.getLoggedInUser().pipe(
      take(1),
      // --- Step 1: Get User and Set Up Initial Vars ---
      switchMap((user) => {
        if (!user) {
          return throwError(() => new Error('User not logged in'));
        }
        userId = user.uid;
        orgCode = user.defaults?.orgCode;
        const storagePath = PathConstants.getStoragePath(user.uid, caseId);
        safeFileName = `${Date.now()}_${file.name}`;
        filePath = `${storagePath}/${safeFileName}`;
        storageRef = ref(this.storage, filePath);

        // --- Step 2: Extract PDF page count (async) ---
        return from(extractPdfPageCount(file)).pipe(
          map((count) => {
            pageCount = count;
            return user;
          })
        );
      }),
      // --- Step 3: Upload File ---
      switchMap(() => {
        return new Promise<void>((resolve, reject) => {
          from(uploadBytes(storageRef, file))
            .pipe(
              takeUntil(cancellationSubject),
              catchError((err) => {
                reject(err);
                return throwError(() => err);
              })
            )
            .subscribe({
              next: () => {
                progressSubject.next({
                  fileName: file.name,
                  percentage: 100,
                  bytesTransferred: file.size,
                  totalBytes: file.size,
                  status: 'uploading',
                });
                resolve();
              },
              error: (err) => reject(err),
            });
        });
      }),
      // --- Step 4: Get Download URL ---
      switchMap(() => {
        return from(getDownloadURL(storageRef)).pipe(takeUntil(cancellationSubject));
      }),
      // --- Step 5: Persist Metadata to Firestore ---
      switchMap((url) => {
        downloadURL = url;
        const documentsPath = PathConstants.getFileCollectionPath(userId, caseId);
        const documentsCollectionRef = collection(this.firestore, documentsPath);

        documentMetadata = {
          name: file.name,
          fileName: safeFileName,
          downloadURL,
          size: file.size,
          type: file.type,
          uploadDate: new Date().toISOString(),
          uploadedBy: userId,
          deleted: false,
          path: filePath,
          category: 'Others',
          pageCount: pageCount, // Now populated!
        };

        return from(addDoc(documentsCollectionRef, documentMetadata)).pipe(takeUntil(cancellationSubject));
      }),
      // --- Step 6: Get the Created Doc Back ---
      switchMap((docRef) => {
        newDocRef = docRef;
        return from(getDoc(docRef)).pipe(takeUntil(cancellationSubject));
      }),
      // --- Step 7: Call Cloud Function ---
      switchMap((docSnap) => {
        const saved = docSnap.exists() ? docSnap.data() : {};
        combinedMetadata = { ...documentMetadata, ...(saved || {}), id: newDocRef.id };

        const payload = {
          orgCode,
          caseId,
          userId: userId,
          docIds: [newDocRef.id],
          pageCount: pageCount, // Pass pageCount to backend
        };
        const callable = httpsCallable(this.functions, 'uploadDocumentToProvider');

        return from(callable(payload)).pipe(
          takeUntil(cancellationSubject),
          map((providerResponse) => ({
            downloadURL,
            metadata: combinedMetadata,
            docId: newDocRef.id,
            pageCount: pageCount,
            providerResponse,
          }))
        );
      }),
      // --- Step 8: Emit completion and handle errors ---
      map((result) => {
        progressSubject.next({
          fileName: file.name,
          percentage: 100,
          bytesTransferred: file.size,
          totalBytes: file.size,
          status: 'completed',
        });
        progressSubject.complete();
        return result;
      }),
      catchError((err) => {
        // Check if error is from cancellation
        if (err && typeof err === 'object' && 'name' in err && (err as any).name === 'cancelled') {
          // Already handled in cancel() function
          return throwError(() => new Error('Upload cancelled by user'));
        }

        progressSubject.next({
          fileName: file.name,
          percentage: 0,
          bytesTransferred: 0,
          totalBytes: file.size,
          status: 'failed',
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
        });
        progressSubject.complete();

        console.error('uploadDocument error:', err);
        return throwError(() => err);
      })
    );

    // Return upload observable, progress observable, and cancel function
    return {
      upload$,
      progress$: progressSubject.asObservable().pipe(throttleTime(100)),
      cancel,
    };
  }

  deleteDocument(caseId: string, documentId: string): any {
    return firstValueFrom(
      this.authService.getLoggedInUser().pipe(
        switchMap((user) => {
          if (!user) {
            return of([]);
          }
          const docRef = doc(this.firestore, PathConstants.getFilePath(user.uid, caseId, documentId));
          const dataToUpdate = { deleted: true };
          console.log('Deleting document:', docRef.path);

          return updateDoc(docRef, dataToUpdate);
        })
      )
    );
  }

  private _convertTimestampsToDates(docData: any): Document {
    if (!docData) return docData;

    const convertedDoc: Document = { ...docData };

    const dateFields = ['uploadDate', 'updatedOn']; // Add other timestamp fields if any

    dateFields.forEach((field) => {
      if (convertedDoc[field] && typeof convertedDoc[field].toDate === 'function') {
        convertedDoc[field] = convertedDoc[field].toDate();
      }
    });

    return convertedDoc;
  }
}
